import React, { useRef, useState } from "react";

import { Stack } from "@mui/system";

import { ManuallyTrackedAccount } from "@fartherfinance/frontend/api/Accounts/Types";
import { AltAssetId } from "@fartherfinance/frontend/api/Types";

import DeleteModal from "@src/sharedComponents/Modal/DeleteModal/DeleteModal";
import ThreeDotsPopoverMenu from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/Menu";
import MenuItem from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/MenuItem";

import styles from "./Header.module.css";

interface Props {
  manualAsset: ManuallyTrackedAccount | null;
  onDeleteAccount: (accountId: AltAssetId) => Promise<void>;
}

const Header = (props: Props): JSX.Element => {
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  if (props.manualAsset === null) {
    return <></>;
  }

  const { assetName, assetType, assetValue, id } = props.manualAsset;

  const assetTypeNormalized =
    assetType === "Collectable" ? "Collectible" : assetType;

  const handleOnClickDelete = () => {
    setShowPopover(false);
    setShowDeleteModal(true);
  };

  const onShowPopover = () => setShowPopover(true);
  const onHidePopover = () => setShowPopover(false);

  const handleDeleteAccount = async () => {
    setShowDeleteModal(false);
    await props.onDeleteAccount(id);
  };

  return (
    <div className={styles.container}>
      <Stack gap={"4px"} direction="row" alignItems="flex-start">
        <div>
          <div className={styles.title}>{assetName}</div>
          <div className={styles.subtleText}>{assetTypeNormalized}</div>
        </div>

        <ThreeDotsPopoverMenu
          anchorRef={anchorRef}
          isOpen={showPopover}
          onOpen={onShowPopover}
          onClose={onHidePopover}
          className={styles.icon}
        >
          <MenuItem onClick={handleOnClickDelete}>Delete</MenuItem>
        </ThreeDotsPopoverMenu>
      </Stack>

      <div className={styles.valueContainer}>
        <div className={styles.subtleText}>Value</div>
        <div className={styles.value}>
          {assetValue.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </div>
      </div>

      {showDeleteModal && (
        <DeleteModal
          title="Delete Account"
          text="Are you sure you want to delete this account? This action cannot be undone."
          deleteButtonText="Delete"
          onDelete={handleDeleteAccount}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default Header;
